import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    Linking,
    Image,
    Modal,
    View
} from "react-native";
import type {
    NewsDetailPayloadType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    MarkdownViewer,
    PageContainer,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    RouteProp
} from "@react-navigation/native";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ExternalLinkIcon,
    ArrowLeftIcon,
    CalendarIcon,
    LinkIcon,
    XIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Clipboard from "@react-native-clipboard/clipboard";
import Svg, {
    Circle,
    Path,
    Rect
} from "react-native-svg";
import {
    APP_URL
} from "../../../../services/restAPI/constants";
import ShareInstagramModal from "../../../../components/shareInstagramModal";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const CustomInstagramIcon = ({
    color = "#E1306C",
    size = 20
}: {
    color?: string;
    size?: number;
}) => {
    return <Svg
        strokeLinejoin="round"
        strokeLinecap="round"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke={color}
        height={size}
        width={size}
        fill="none"
    >
        <Rect
            height={20}
            width={20}
            rx={5}
            ry={5}
            x={2}
            y={2}
        />
        <Path
            d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"
        />
        <Circle
            fill={color}
            cx={17.5}
            cy={6.5}
            r={1.5}
        />
    </Svg>;
};

const resolvePhotoUrl = (
    photo?: { path?: string; url?: string } | string,
    authToken?: string
): string => {
    if(!photo) {
        return "";
    }

    let targetUrl = "";

    if(typeof photo === "string") {
        targetUrl = photo;
    } else if(photo.url && photo.url.startsWith("http")) {
        targetUrl = photo.url;
    } else if(photo.path) {
        targetUrl = photo.path;
    }

    if(!targetUrl) {
        return "";
    }

    if(targetUrl.includes("/uploads/drive/")) {
        targetUrl = targetUrl.replace("/uploads/drive/", "/drive/");
    }

    const isDrive = targetUrl.includes("/drive/");
    if(isDrive) {
        if(!targetUrl.startsWith("http")) {
            const cleanPath = targetUrl.startsWith("/") ? targetUrl : `/${targetUrl}`;
            targetUrl = `${process.env.STORAGE_URL}${cleanPath}`;
        }
        if(authToken && !targetUrl.includes("token=")) {
            const separator = targetUrl.includes("?") ? "&" : "?";
            targetUrl = `${targetUrl}${separator}token=${authToken}`;
        }
        return targetUrl;
    }

    if(targetUrl.startsWith("http")) {
        return targetUrl;
    }

    const cleanPath = targetUrl.startsWith("/uploads")
        ? targetUrl
        : `/uploads${targetUrl.startsWith("/") ? "" : "/"}${targetUrl}`;

    return `${process.env.STORAGE_URL}${cleanPath}`;
};

const NewsDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute<RouteProp<RootStackParamList, "NewsDetail">>();
    const {
        newsID
    } = route.params || {};

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext();
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        data,
        setData
    ] = useState<NewsDetailPayloadType | null>(null);

    const [
        isLightboxVisible,
        setIsLightboxVisible
    ] = useState<boolean>(false);

    const [
        selectedLightboxImage,
        setSelectedLightboxImage
    ] = useState<string>("");

    const [
        isInstagramModalVisible,
        setIsInstagramModalVisible
    ] = useState<boolean>(false);

    const [
        authToken,
        setAuthToken
    ] = useState<string>("");

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    useEffect(() => {
        AsyncStorage.getItem("authToken").then((token: string | null) => {
            if(token) {
                setAuthToken(token);
            }
        });
    }, []);

    const fetchNews = useCallback(async () => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action("getNewsById", {
                newsID: newsID as string
            });
            if (response.payload) {
                setData(response.payload as unknown as NewsDetailPayloadType);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [newsID]);

    useEffect(() => {
        if(newsID) {
            fetchNews();
        } else {
            navigation.goBack();
        }
    }, [newsID]);

    const handleCopyShareLink = () => {
        const shareUrl = `${APP_URL}/news/${newsID}`;
        Clipboard.setString(shareUrl);
        if(typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(shareUrl).catch(() => {});
        }
        NCoreUIKitSnackBar.open({
            title: localize("share-link-copied")
        });
    };

    if(loading) {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl,
                        flex: 1
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    if (!data) {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={[
                    stylesheet.emptyContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("news-not-found" as never) || "Haber bulunamadı"}
                </Text>
            </View>
        </PageContainer>;
    }

    const currentLangData = data.data.find(d => d.language === activeLocale) || data.data[0];

    return <PageContainer
        isScrollable={true}
    >
        <ScrollView
            showsVerticalScrollIndicator={false}
            style={stylesheet.contentContainer}
        >
            <View
                style={[
                    stylesheet.topBarContainer,
                    {
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Button
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={() => navigation.goBack()}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <View
                    style={[
                        stylesheet.topBarActions,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        customTextColor={colors.project.brandWhite}
                        icon={({
                            size
                        }) => <LinkIcon
                            color={colors.project.brandWhite}
                            size={size}
                        />}
                        title={localize("copy-share-link")}
                        customBorderColor="secondaryButton"
                        onPress={handleCopyShareLink}
                        customColor="secondaryButton"
                        spreadBehaviour="free"
                        variant="filled"
                    />
                    {
                        data.instagramPostUrl ?
                            <Button
                                onPress={() => {
                                    if(data.instagramPostUrl) {
                                        if(typeof window !== "undefined") {
                                            window.open(data.instagramPostUrl, "_blank");
                                        } else {
                                            Linking.openURL(data.instagramPostUrl);
                                        }
                                    }
                                }}
                                customTextColor={colors.project.brandWhite}
                                title={localize("instagram-view-post")}
                                icon={({
                                    size
                                }) => <ExternalLinkIcon
                                    color={colors.project.brandWhite}
                                    size={size}
                                />}
                                customBorderColor="secondaryButton"
                                customColor="secondaryButton"
                                spreadBehaviour="free"
                                variant="outline"
                            />
                        :
                            null
                    }
                    <Button
                        onPress={() => setIsInstagramModalVisible(true)}
                        customTextColor={colors.project.brandWhite}
                        title={localize("share-on-instagram")}
                        icon={() => <CustomInstagramIcon
                            color={colors.project.brandWhite}
                            size={18}
                        />}
                        spreadBehaviour="free"
                        customColor="primary"
                        variant="filled"
                    />
                </View>
            </View>
            {data.coverPhotoPath && (
                <TouchableOpacity
                    onPress={() => {
                        setSelectedLightboxImage(resolvePhotoUrl(data.coverPhotoPath, authToken));
                        setIsLightboxVisible(true);
                    }}
                    activeOpacity={0.8}
                >
                    <SmartCoverImage
                        url={resolvePhotoUrl(data.coverPhotoPath, authToken)}
                        containerStyle={[
                            stylesheet.coverImage,
                            {
                                marginBottom: spaces.spacingLg,
                                borderRadius: radiuses.md
                            }
                        ]}
                    />
                </TouchableOpacity>)}
            <View
                style={stylesheet.headerContainer}
            >
                <Text
                    variant="displaySmallSize"
                >
                    {currentLangData?.title || ""}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.metaContainer,
                    {
                        marginBottom: spaces.spacingLg,
                        marginTop: spaces.spacingMd,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.metaItem,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <CalendarIcon
                        color={colors.content.text.mid}
                        size={16}
                    />
                    <Text
                        customColor={colors.content.text.mid}
                        variant="labelMediumSize"
                    >
                        {new Date(data.createdAt).toLocaleDateString()}
                    </Text>
                </View>
            </View>
            {
                currentLangData?.content ?
                    <MarkdownViewer
                        content={currentLangData.content}
                    />
                :
                    null
            }
            {data.postPhotos && data.postPhotos.length > 0 && (
                <View
                    style={[
                        stylesheet.galleryContainer,
                        {
                            marginBottom: spaces.spacingXl,
                            marginTop: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        style={{
                            marginBottom: spaces.spacingMd
                        }}
                        variant="titleMediumSize"
                    >
                        {localize("news-photos-title")}
                    </Text>
                    <ScrollView
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{
                            gap: spaces.spacingMd
                        }}
                        horizontal={true}
                    >
                        {data.postPhotos.map((photo, index) => {
                            const photoUrl = resolvePhotoUrl(photo, authToken);
                            return <TouchableOpacity
                                onPress={() => {
                                    setSelectedLightboxImage(photoUrl);
                                    setIsLightboxVisible(true);
                                }}
                                key={`${photo.path || index}-${index}`}
                                activeOpacity={0.8}
                            >
                                <SmartCoverImage
                                    containerStyle={[
                                        stylesheet.galleryThumbnail,
                                        {
                                            borderColor: colors.project.brandBorder,
                                            borderWidth: borders.seperator,
                                            borderRadius: radiuses.md
                                        }
                                    ]}
                                    url={photoUrl}
                                />
                            </TouchableOpacity>;
                        })}
                    </ScrollView>
                </View>)}
        </ScrollView>
        <Modal
            onRequestClose={() => setIsLightboxVisible(false)}
            visible={isLightboxVisible}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.system.scrim
                    }
                ]}
            >
                <TouchableOpacity
                    onPress={() => setIsLightboxVisible(false)}
                    style={[
                        stylesheet.modalCloseButton,
                        {
                            right: spaces.spacingMd,
                            top: spaces.spacingMd
                        }
                    ]}
                >
                    <XIcon
                        color={colors.project.brandWhite}
                        size={32}
                    />
                </TouchableOpacity>
                <Image
                    source={{
                        uri: resolvePhotoUrl(selectedLightboxImage, authToken)
                    }}
                    style={stylesheet.lightboxImage}
                    resizeMode="contain"
                />
            </View>
        </Modal>
        <ShareInstagramModal
            isAlreadyPublished={Boolean(data.instagramPostUrl || data.instagramPostId)}
            onSuccess={(url, id) => {
                setData(prev => prev ? {
                    ...prev,
                    instagramPublishedAt: new Date().toISOString(),
                    instagramPostUrl: url,
                    instagramPostId: id
                } : null);
            }}
            onClose={() => setIsInstagramModalVisible(false)}
            shortContent={currentLangData?.shortContent}
            coverPhotoPath={data.coverPhotoPath}
            title={currentLangData?.title || ""}
            isVisible={isInstagramModalVisible}
            postPhotos={data.postPhotos}
            availableData={data.data}
            targetID={data._id}
            targetType="news"
        />
    </PageContainer>;
};
export default NewsDetail;
